// Implements: F-MCK-002, F-MCK-016, F-UIX-009, F-UIX-007
/**
 * The gallery as a table (the mockup's list view): name with its tags, scope, the day it changed and
 * the star and menu. Pure presentation. Mockup v3 P8: the "Versiones" column shows the current
 * version ("v8"), empty before the first generation.
 */

import type { ReactElement } from "react";
import { Link } from "react-router";

import { useTranslation } from "../../../shared/i18n";
import type { MockupSummary } from "../../../types/api";
import { dayText } from "../../../shared/ui/dayText";
import { Badge } from "../../../shared/ui/atoms/Badge";
import { Icon } from "../../../shared/ui/atoms/Icon";
import type { MockupActions } from "./mockupActions";
import { MockupActionsMenu } from "./MockupActionsMenu";
import styles from "./MockupTable.module.css";
import { VersionBadge } from "./VersionBadge";

export interface MockupTableProps {
  readonly mockups: readonly MockupSummary[];
  readonly scopeLabelOf: (mockup: MockupSummary) => string;
  readonly actions: MockupActions;
}

export function MockupTable({ mockups, scopeLabelOf, actions }: MockupTableProps): ReactElement {
  const { t } = useTranslation();
  return (
    <div className={styles.wrap}>
      <table className={styles.table} aria-label={t("gallery.table.label")}>
        <thead>
          <tr>
            <th scope="col">{t("gallery.col.name")}</th>
            <th scope="col">{t("gallery.col.scope")}</th>
            <th scope="col">{t("gallery.col.version")}</th>
            <th scope="col">{t("gallery.col.updated")}</th>
            <th scope="col" className={styles.actionsHead}>
              <span className={styles.srOnly}>{t("gallery.col.actions")}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {mockups.map((mockup) => (
            <tr key={mockup.id}>
              <td>
                <Link className={styles.name} to={`/lienzo/${encodeURIComponent(mockup.id)}`}>
                  {mockup.name}
                </Link>
                {mockup.tags.length === 0 ? null : (
                  <div className={styles.tags}>{mockup.tags.map((tag) => `#${tag}`).join(" ")}</div>
                )}
              </td>
              <td>
                <Badge tone="outline">
                  <Icon name={mockup.scope_kind === "personal" ? "user" : "users"} size="sm" />
                  {scopeLabelOf(mockup)}
                </Badge>
              </td>
              <td>
                <VersionBadge number={mockup.current_version_number} />
              </td>
              <td className={styles.date}>
                <time dateTime={mockup.updated_at}>{dayText(mockup.updated_at)}</time>
              </td>
              <td className={styles.actions}>
                <MockupActionsMenu mockup={mockup} actions={actions} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
